import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import TopNav from '../../components/TopNav'
import LoadError from '../../components/LoadError'
import useCustomerList from '../../utils/useCustomerList'
import { loadErrorMessage, settledList } from '../../utils/loadState'
import { getThread, updateThread, deleteThread, runThread, getThreadExecutions } from '../../api'
import './ThreadDetail.css'

export default function ThreadDetail() {
  const { threadId } = useParams()
  const navigate = useNavigate()

  const [thread, setThread] = useState(null)
  const [executions, setExecutions] = useState([])
  const [runCustomerId, setRunCustomerId] = useState('')
  const { customers, error: customersError, reload: reloadCustomers } = useCustomerList(list => {
    if (list.length > 0) setRunCustomerId(prev => prev || list[0].customer_id)
  })
  const [loading, setLoading] = useState(true)
  const [running, setRunning] = useState(false)
  const [editing, setEditing] = useState(false)
  const [editName, setEditName] = useState('')
  const [editDesc, setEditDesc] = useState('')
  // A failed load is an error with a Retry -- never "Thread not found." or
  // "No executions yet" (item 201).
  const [threadError, setThreadError] = useState(null)
  const [executionsError, setExecutionsError] = useState(null)

  async function load() {
    setLoading(true)
    try {
      const [t, e] = await Promise.allSettled([
        getThread(threadId),
        getThreadExecutions(threadId),
      ])
      if (t.status === 'fulfilled') {
        setThread(t.value)
        setThreadError(null)
        setEditName(t.value.name || '')
        setEditDesc(t.value.description || '')
        setRunCustomerId(t.value.customer_id || '')
      } else {
        setThreadError(loadErrorMessage(t.reason))
      }
      const ex = settledList(e, 'executions')
      setExecutions(ex.items || [])
      setExecutionsError(ex.error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [threadId])

  async function handleRun() {
    if (!runCustomerId) return
    setRunning(true)
    try {
      await runThread(threadId, { customer_id: runCustomerId })
      // Refresh executions
      const e = await getThreadExecutions(threadId)
      setExecutions(Array.isArray(e) ? e : e?.executions || [])
    } catch (err) {
      console.error('Run failed:', err)
      alert('Run failed: ' + err.message)
    } finally {
      setRunning(false)
    }
  }

  async function handleSave() {
    try {
      await updateThread(threadId, { name: editName, description: editDesc })
      setThread(prev => ({ ...prev, name: editName, description: editDesc }))
      setEditing(false)
    } catch (err) {
      alert('Save failed: ' + err.message)
    }
  }

  async function handleDelete() {
    if (!confirm('Delete this thread permanently?')) return
    try {
      await deleteThread(threadId)
      navigate('/threads')
    } catch (err) {
      alert('Delete failed: ' + err.message)
    }
  }

  const stepTypeIcons = {
    query: '🔍', analyze: '📊', compare: '⚖️', export: '📄',
    remediation: '🔧', validation: '✅', gate: '🚧', discovery: '🔎',
  }

  if (loading) {
    return (
      <>
        <TopNav breadcrumbs={[{ label: 'Threads', href: '/threads' }, { label: 'Loading…' }]} />
        <div className="td-loading"><div className="td-spinner" /><p>Loading thread…</p></div>
      </>
    )
  }

  if (!thread && threadError) {
    return (
      <>
        <TopNav breadcrumbs={[{ label: 'Threads', href: '/threads' }, { label: 'Error' }]} />
        <div className="td-loading">
          <LoadError what="this thread" error={threadError} onRetry={load} />
        </div>
      </>
    )
  }

  if (!thread) {
    return (
      <>
        <TopNav breadcrumbs={[{ label: 'Threads', href: '/threads' }, { label: 'Not Found' }]} />
        <div className="td-loading">
          <p>Thread not found.</p>
          <button className="td-btn" onClick={() => navigate('/threads')}>Back to Threads</button>
        </div>
      </>
    )
  }

  const breadcrumbs = [
    { label: 'Threads', href: '/threads' },
    { label: thread.name || 'Thread' },
  ]
  const navActions = (
    <>
      <button className="nav-btn" onClick={() => setEditing(!editing)}>{editing ? 'Cancel' : 'Edit'}</button>
      <button className="nav-btn" style={{ color: 'var(--red)' }} onClick={handleDelete}>Delete</button>
      
    </>
  )

  return (
    <>
      <TopNav breadcrumbs={breadcrumbs} actions={navActions} />

      <div className="td-page">
        <div className="td-grid">
          {/* Left: Thread Info + Steps */}
          <div className="td-col">
            {/* Header */}
            <div className="td-card">
              {editing ? (
                <>
                  <input
                    className="td-edit-input td-edit-name"
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    placeholder="Thread name"
                  />
                  <textarea
                    className="td-edit-input td-edit-desc"
                    value={editDesc}
                    onChange={e => setEditDesc(e.target.value)}
                    placeholder="Description"
                    rows={2}
                  />
                  <button className="td-save-btn" onClick={handleSave}>Save Changes</button>
                </>
              ) : (
                <>
                  <div className="td-header-top">
                    <span className="td-category">{thread.category || 'general'}</span>
                    <span className="td-status">{thread.status || 'draft'}</span>
                  </div>
                  <h2 className="td-name">{thread.name || 'Untitled'}</h2>
                  <p className="td-desc">{thread.description || 'No description'}</p>
                  <div className="td-meta">
                    {thread.vendor && <span>Vendor: {thread.vendor}</span>}
                    <span>Created: {new Date(thread.created_at).toLocaleDateString()}</span>
                    {thread.run_count > 0 && <span>{thread.run_count} runs</span>}
                  </div>
                </>
              )}
            </div>

            {/* Steps */}
            <div className="td-card">
              <h3 className="td-card-title">Steps ({thread.steps?.length || 0})</h3>
              {(!thread.steps || thread.steps.length === 0) ? (
                <p className="td-empty">No steps defined</p>
              ) : (
                <div className="td-steps">
                  {thread.steps.map((step, i) => (
                    <div key={i} className="td-step">
                      <div className="td-step-num">{i + 1}</div>
                      <div className="td-step-content">
                        <div className="td-step-type">
                          {stepTypeIcons[step.type] || '📌'} {step.type || 'step'}
                        </div>
                        <div className="td-step-text">{step.content || step.description || 'No content'}</div>
                        {step.parameters && Object.keys(step.parameters).length > 0 && (
                          <div className="td-step-params">
                            Params: {Object.keys(step.parameters).join(', ')}
                          </div>
                        )}
                        {step.requires_confirmation && (
                          <span className="td-step-gate">Requires confirmation</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right: Run + Executions */}
          <div className="td-col">
            {/* Run Panel */}
            <div className="td-card">
              <h3 className="td-card-title">Run Thread</h3>
              <div className="td-run-form">
                <label className="td-run-label">Customer</label>
                <select
                  className="td-run-select"
                  value={runCustomerId}
                  onChange={e => setRunCustomerId(e.target.value)}
                >
                  {customers.map(c => (
                    <option key={c.customer_id} value={c.customer_id}>{c.company_name}</option>
                  ))}
                </select>
                {customersError && <LoadError inline what="customers" error={customersError} onRetry={reloadCustomers} />}
                <button className="td-run-btn" onClick={handleRun} disabled={running || !runCustomerId}>
                  {running ? 'Running…' : '▶ Run Thread'}
                </button>
              </div>
            </div>

            {/* Execution History */}
            <div className="td-card">
              <h3 className="td-card-title">Execution History</h3>
              {executionsError ? (
                <LoadError inline what="executions" error={executionsError} onRetry={load} />
              ) : executions.length === 0 ? (
                <p className="td-empty">No executions yet. Run the thread to see results here.</p>
              ) : (
                <div className="td-exec-list">
                  {executions.map((ex, i) => (
                    <div key={i} className="td-exec-item">
                      <div className="td-exec-top">
                        <span className={`td-exec-status ${ex.status || 'complete'}`}>{ex.status || 'complete'}</span>
                        <span className="td-exec-date">{new Date(ex.started_at || ex.created_at).toLocaleString()}</span>
                      </div>
                      <div className="td-exec-meta">
                        Customer: {ex.customer_id}
                        {ex.duration_ms && ` · ${(ex.duration_ms / 1000).toFixed(1)}s`}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
